//  Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.
import { Component, OnInit } from '@angular/core';
import { AzureIntegratonSource, IIntegration, PartnerTypes } from '../../../../types/integrations';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { SelectedOrgMethod } from '../../../../shared/selectedOrg';
import { ActivatedRoute, Router } from '@angular/router';
import { D3eService } from '../../../../services/d3e-service';
import { HeaderService } from '../../../header.service';
import { AlertDialogComponent } from '../../../dialogs/alert/alert.component';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { CustomValidators } from '../../../shared/validators/custom-validators';
import { UserService } from '../../../../services/user-service';
import { NotificationService } from '../../../../services/notification-service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DeactivatableComponent } from '../../../guards/page.guard';
import { withLoading } from '../../../../shared/misc';
import { BehaviorSubject } from 'rxjs';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-active-directory',
    templateUrl: './active-directory.component.html',
    styleUrls: ['./active-directory.component.scss'],
    standalone: false
})
export class ActiveDirectoryComponent implements OnInit, DeactivatableComponent {

  private readonly PARTNER_ID = PartnerTypes.ACTIVE_DIRECTORY;

  formGroup: UntypedFormGroup;
  integration: IIntegration;
  integrationId: string = null;
  loading = false;
  conflict = false;
  loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  selectedOrgId: string;
  hasUserBeenSet = false;
  toggleGroupAppearance: 'legacy' | 'standard' = 'standard'; // make input?
  private fConfigSaved: string;
  private fSaveError: string;
  private fChangesBeingSaved: string;
  source = AzureIntegratonSource.CIGENT_APP;
  disableTenantId = false;
  tenant_control: AbstractControl;
  constructor( private route: ActivatedRoute,
               private d3eService: D3eService,
               private router: Router,
               private headerService: HeaderService,
               private formBuilder: UntypedFormBuilder,
               public dialog: MatDialog,
               private translate: TranslateService,
               private userService: UserService,
               private notification: NotificationService) {

    this.translate.get(['CONFIG_SAVED', 'CHANGES_BEING_SAVED', 'REPORT_SAVE_ERROR']).subscribe( vals => {
      this.fConfigSaved = vals['CONFIG_SAVED'];
      this.fChangesBeingSaved = vals['CHANGES_BEING_SAVED'];
      this.fSaveError = vals['REPORT_SAVE_ERROR'];
    });

    this.formGroup = this.formBuilder.group({
      tenant_id: ['', Validators.compose([Validators.required, CustomValidators.uuidValidator()])],
      enable_all: ['', Validators.compose(
          [CustomValidators.conditionalValidator(
              (attrFormControl) =>
                  (this.formGroup.get('groups') as UntypedFormArray).length === 0 && (!attrFormControl.value),
              'roleError')])
      ],
      groups: this.formBuilder.array([])
    });

  }

  touchOtherAttr(attr_name): ValidatorFn {
    return (control: AbstractControl) => {
      this.formGroup.controls[attr_name].updateValueAndValidity();
      return null;
    };
  }

  private createGroupFormatGroup(group: string, role: string) {
    return new UntypedFormGroup({
      'group_id': new UntypedFormControl(group.trim(), [Validators.required, CustomValidators.uuidValidator(), this.touchOtherAttr('enable_all')]),
      'role': new UntypedFormControl({value: role, disabled: false}, Validators.nullValidator),
    });
  }

  ngOnInit(): void {

    this.headerService.orgSelectedChanged
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        this.selectedOrgId = value;
        if ( this.hasUserBeenSet ) {
            // TODO: launch the dirty flag window
            this.router.navigateByUrl('/integrations');
        }
        this.hasUserBeenSet = true;
    });

    this.route.params.subscribe(params => {
      this.integrationId = params.id;
      if (this.integrationId === 'new') {
        const groups = this.formGroup.get('groups') as UntypedFormArray;
        groups.push(this.createGroupFormatGroup('', 'help-desk'));

        const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
        this.userService.getUser(selectedOrg)
          .pipe(untilDestroyed(this))
          .subscribe(user => {
            if ( user.email ) {
              const email = user.email;
              if ( email ) {
                const domain = email.split('@').at(-1);
                if ( domain ) {
                   this.formGroup.patchValue({domain_name: domain});
                }
              }
            }
            this.loading = false;
        }, error => { this.loading = false; } );
      } else {
        const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

        this.d3eService.get_integration(this.integrationId, selectedOrg)
          .pipe(
              withLoading(isLoading => this.loading$.next(isLoading)),
              untilDestroyed(this)
          )
          .subscribe(integration => {
            if ( integration.config.source === AzureIntegratonSource.AZURE_SERVICE ) {
              this.source = AzureIntegratonSource.AZURE_SERVICE;
              this.disableTenantId = true;
            } else {
              this.source = AzureIntegratonSource.CIGENT_APP;
              this.disableTenantId = false;
            }

            if (integration.partner_id !== this.PARTNER_ID) {
              this.router.navigateByUrl('/integrations');
            }
            this.formGroup.controls['tenant_id'].setValue(integration.config.tenant_id);
            this.formGroup.controls['enable_all'].setValue(integration.config.enable_all);
            for ( const group of integration.config.groups ) {
              const groups = this.formGroup.get('groups') as UntypedFormArray;

              groups.push(this.createGroupFormatGroup(group.group_id, group.role));
            }

            this.integration = integration;
        });
      }
    });
  }

  get controls()  {
    return (this.formGroup.get('groups') as UntypedFormArray).controls;
  }

  // Implementation of interface DeactivatableComponent in page.guard.ts
  getIsDirty(): boolean {
    return this.formGroup.dirty;
  }


  // Implementation of interface DeactivatableComponent in page.guard.ts
  canDeactivateResult(result: boolean): void {
    // no-op
  }

  // Implementation of interface DeactivatableComponent in page.guard.ts
  userValueChanged(): void {
    // no-op
  }

  save() {
    this.notification.showUpdate(this.fChangesBeingSaved);
    const data = this.formGroup.value;
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

    if ( this.integrationId === 'new' ) {
      const integration: IIntegration = {
        display_name: 'Microsoft Azure Active Directory',
        enabled: true,
        partner_id: PartnerTypes.ACTIVE_DIRECTORY,
        config: {
          tenant_id: data.tenant_id,
          enable_all: data.enable_all,
          groups: data.groups,
          source: this.source
        }
      };
      this.d3eService.add_integration(integration, selectedOrg)
        // We do NOT want to destroy early, let this finish.
        // Do NOT use .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.reportSaveSuccess();
          this.formGroup.reset();
          this.goToIntegrations();
        }, error => {
          this.reportError(error.error);
      });
    } else {
      this.integration.config = {
          tenant_id: data.tenant_id,
          enable_all: data.enable_all,
          groups: data.groups,
          source: this.source
        };

      this.d3eService.update_integration(this.integration, selectedOrg)
        // We do NOT want to destroy early, let this finish.
        // Do NOT use .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.reportSaveSuccess();
          this.formGroup.reset();
          this.goToIntegrations();
        }, error => {
          this.reportError(error);
      });
    }
  }

  private reportError(error_attr) {

    if (error_attr === 'tenant_id') {
      this.tenant_control = this.formGroup.controls['tenant_id'];
      this.tenant_control.setErrors({'duplicate': true});
      this.tenant_control.markAsTouched();
    }
    this.reportSaveError.bind(this);
  }

  cancel() {
    if (this.formGroup.touched) {
      const dialogRef = this.dialog.open(AlertDialogComponent, {
        width: '450px',
        data: {
          title: this.translate.instant('DISCARD_CHANGES'),
          content: this.translate.instant('DISCARD_CHANGES_CONTENT'),
          rejectBtnName: this.translate.instant('NO'),
          acceptBtnName: this.translate.instant('YES'),
          showRejectBtn: true,
        },
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.formGroup.reset();
          this.router.navigateByUrl('/integrations');
        }
      });
    } else {
      this.router.navigateByUrl('/integrations');
    }
  }

  onAddGroupClick() {
    const groupId = '';
    const role = `help-desk`;
    const groups = this.formGroup.get('groups') as UntypedFormArray;

    if (groups.value.some(v => v.group === '')) {
      // TODO: Warn user they have a new group that needs a group ID before creating a new group.
      return;
    }
    groups.push(this.createGroupFormatGroup(groupId, role));
  }

  deleteGroup(i: number, $event: MouseEvent) {
    const groups = this.formGroup.get('groups') as UntypedFormArray;
    groups.removeAt(i);
    this.formGroup.controls['enable_all'].updateValueAndValidity();
  }

  reportSaveSuccess() {
    this.notification.showSuccess(this.fConfigSaved);
  }

  reportSaveError(error) {
    this.notification.showError(this.fSaveError);
  }

  goToIntegrations() {
    this.router.navigateByUrl('/integrations');
  }

  hasUuidError(i: number) {
    const formGroup: UntypedFormArray = this.formGroup.get('groups') as UntypedFormArray;
    const control = formGroup.controls[i].get('group_id');
    return !!(control.errors && control.errors['invalidUuid']);
  }
}
